import { Component, computed, input, OnInit, output, signal, viewChild } from '@angular/core';
import { delay, shuffle } from '@gametailors/web-core/misc';
import { RidDraggable } from '../../components/rid-draggable/rid-draggable';
import { RidDropZone } from '../../components/rid-drop-zone/rid-drop-zone';
import { RidKlankCard } from '../../components/rid-klank-card/rid-klank-card';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidSpacer } from '../../components/rid-spacer/rid-spacer';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidSfxDirective } from '../../directives/rid-sfx.directive';
import { AudioEffectPlayer, AudioPlayer } from '../../utils/audio.utils';

export interface SnakeWord {
  words: [string, string];
  audio: string;
}

interface SnakeOption {
  word: string;
  available: boolean;
}

const CARD_WIDTH_REM = 8.5;
const FEEDBACK_DELAY_MS = 1000;
const LEAVE_ANIMATION_MS = 400;

@Component({
  selector: 'rid-word-snake',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidOverlayFeedback,
    RidKlankCard,
    RidDraggable,
    RidDropZone,
    RidSpacer,
    RidMechanicTitle,
    RidSfxDirective,
  ],
  templateUrl: './rid-word-snake.html',
  host: {
    class: 'block',
  },
})
export class RidWordSnake implements OnInit {
  snakes = input.required<SnakeWord[][]>();

  snakeIndex = signal(0);
  showAssignment = signal(false);
  showOptions = signal(false);
  assignment = signal<{ options: SnakeOption[]; snake: string[] }>({ options: [], snake: [] });
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  dropZone = viewChild.required<RidDropZone>('dropZone');
  dragIndex = signal<number | undefined>(undefined);
  currentSnake = computed(() => this.snakes()[this.snakeIndex()]);

  snakeTransform = signal(0);
  showPlus = signal(true);
  showDropZone = signal(true);

  finished = output<void>();

  ngOnInit(): void {
    this.initializeSnake(this.snakeIndex());
    this.showAssignment.set(true);
  }

  private initializeSnake(index: number) {
    const snake = this.snakes()[index];
    const options = shuffle(snake.map((entry) => ({ word: entry.words[1], available: true })));
    this.assignment.set({ options, snake: [snake[0].words[0]] });
    this.showOptions.set(false);
  }

  revealOptions() {
    this.showOptions.set(true);
  }

  onDragStart(optionIndex: number) {
    this.dragIndex.set(optionIndex);
  }

  onDragMove(draggable: RidDraggable, point: { x: number; y: number }) {
    this.dropZone().handleMove(draggable, point);
  }

  onDragRelease(draggable: RidDraggable, optionIndex: number) {
    this.dragIndex.set(undefined);

    if (this.dropZone().isSnapped(draggable)) {
      this.onDropped(draggable, optionIndex);
    }
  }

  onDropped(draggable: RidDraggable, optionIndex: number) {
    const { options, snake } = this.assignment();
    const expectedWord = this.currentSnake()[snake.length - 1].words[1];

    if (options[optionIndex].word !== expectedWord) {
      draggable.snapToOrigin();
      AudioEffectPlayer.play('INCORRECT');
      this.overlayFeedback().showIncorrect();
      return;
    }

    this.handleCorrectDrop(optionIndex);
  }

  private async handleCorrectDrop(optionIndex: number) {
    const assignment = this.assignment();
    const snake = this.currentSnake();
    const newOptions = assignment.options.map((o, i) => (i === optionIndex ? { ...o, available: false } : o));
    const newSnake = [...assignment.snake, assignment.options[optionIndex].word];

    this.showPlus.set(false);
    this.showDropZone.set(false);
    this.assignment.set({ options: newOptions, snake: newSnake });
    AudioEffectPlayer.play('CORRECT');
    this.overlayFeedback().showCorrect();
    AudioPlayer.play(snake[assignment.snake.length - 1].audio);

    await delay(FEEDBACK_DELAY_MS);

    this.showPlus.set(true);
    this.showDropZone.set(true);
    AudioEffectPlayer.play('SHORT_SLIDE');
    this.snakeTransform.set(CARD_WIDTH_REM * (newSnake.length - 1));

    if (newSnake.length >= snake.length + 1) {
      await this.goToNextSnake();
    }
  }

  private async goToNextSnake() {
    const nextIndex = this.snakeIndex() + 1;

    if (nextIndex >= this.snakes().length) {
      this.finished.emit();
      return;
    }

    this.showAssignment.set(false);
    this.snakeTransform.set(0);

    await delay(LEAVE_ANIMATION_MS);

    this.snakeIndex.set(nextIndex);
    this.initializeSnake(nextIndex);
    this.showAssignment.set(true);
  }
}
